import { Grid, Typography, WithStyles } from "@material-ui/core";
import * as React from "react";

import { ProductGPIHelper } from "../../helper/ProductGPI.helper";
import { Beneficiary } from "../../types/Beneficiary";
import { Customer } from "../../types/Customer";
import { Form } from "../../types/Form";
import {
    ProductGPI,
    productGPIHealthStateItems,
    productGPISamplingDatesItems,
    productGPISamplingIntervalsItems,
    productGPISignDeclarationItems,
} from "../../types/ProductGPI";
import { Products } from "../../types/Products";
import { productSPRBeneficiaryClauseItems } from "../../types/ProductSPR";
import { Quotation } from "../../types/Quotation";
import { UiFormBuilder } from "../../utils/UiFormBuilder";
import { UpdateRequestBuilder } from "../../utils/UpdateRequestBuilder";
import Contribution from "../Contribution/Contribution";
import InputCheckbox from "../InputCheckbox/InputCheckbox";
import InputRadio from "../InputRadio/InputRadio";
import InputSelect from "../InputSelect/InputSelect.component";
import InputText from "../InputText/InputText";
import { quotationFormStyles } from "./QuotationForm.styles";
import QuotationFormBeneficiaries from "./QuotationFormBeneficiaries";

interface QuotationProductGPIProps extends WithStyles<typeof quotationFormStyles> {
    mode?: "calculator";
    productHelper: ProductGPIHelper;
    customer: Customer;
    quotation: Quotation;
    showError(message: string);
    hideError();
    onChange(product: Products);
}

type QuotationProductGPIState = {
    form: Form<Products>;
};

class QuotationFormProductGPI extends React.Component<QuotationProductGPIProps, QuotationProductGPIState> {
    private formBuilder = null;

    async componentWillMount() {
        this.updateState(this.props);
    }

    componentWillReceiveProps(nextProps: QuotationProductGPIProps) {
        this.updateState(nextProps);
    }

    public onChange(fieldName: string, value: any) {
        const builder = new UpdateRequestBuilder();
        builder.set(`values.${fieldName}`, value);
        let form = builder.exec(this.state.form);
        this.setState({ form: form });
        this.props.onChange(form.values);
    }

    public onChangeBeneficiaries(beneficiaries: Beneficiary[]) {
        let product = this.props.quotation.product as ProductGPI;
        product.beneficiaries = beneficiaries;
        this.props.onChange(product);
    }

    public render() {
        if (!this.state || !this.props.customer || !this.props.customer.birthdate) {
            return null;
        }

        const uiForm = this.formBuilder.build(this.state.form);
        const product: ProductGPI = this.props.quotation.product as ProductGPI;

        return (
            <>
                {/* Mode de prélèvement */}

                <div className={this.props.classes.box}>
                    <Typography
                        variant={this.props.mode === "calculator" ? "headline" : "title"}
                        gutterBottom={true}
                        className={this.props.classes.formTitle}
                    >
                        {this.props.mode === "calculator" ? "Mode de prélèvement" : "4/ Mode de prélèvement"}
                    </Typography>
                    <Grid container={true} spacing={16}>
                        <Grid item={true} sm={12}>
                            <InputRadio {...uiForm.fields.samplingInterval} />
                        </Grid>
                        <Grid item={true} sm={12}>
                            <InputRadio {...uiForm.fields.samplingDate} />
                        </Grid>
                    </Grid>
                </div>

                {/* Garanties */}

                <div className={this.props.classes.box}>
                    <Typography
                        variant={this.props.mode === "calculator" ? "headline" : "title"}
                        gutterBottom={true}
                        className={this.props.classes.formTitle}
                    >
                        {this.props.mode === "calculator" ? "Garanties" : "5/ Garanties"}
                    </Typography>
                    <Grid container={true} spacing={16}>
                        {/* IAD */}

                        <Grid item={true} sm={3} className={this.props.classes.guarantyBox}>
                            <div className={this.props.classes.guarantyBoxCheckbox}>
                                <InputCheckbox {...uiForm.fields.isIAD} />
                            </div>
                            <div className={this.props.classes.guarantyBoxInput}>
                                <InputSelect {...uiForm.fields.IADGuarantee} />
                            </div>
                            <div className={this.props.classes.guarantyBoxResult} />
                        </Grid>

                        {/* Accident */}

                        <Grid item={true} sm={3} className={this.props.classes.guarantyBoxOptional}>
                            <div className={this.props.classes.guarantyBoxCheckbox}>
                                <InputCheckbox {...uiForm.fields.isAccident} />
                            </div>
                            <div className={this.props.classes.guarantyBoxInput}>
                                <InputText {...uiForm.fields.accidentGuarantee} />
                            </div>
                            <div className={this.props.classes.guarantyBoxResult}>
                                {product && product.accidentContribution ? (
                                    <Typography variant="body2" align="center">
                                        {product.accidentContribution} €/an
                                    </Typography>
                                ) : null}
                            </div>
                        </Grid>

                        {/* ITT */}

                        <Grid
                            item={true}
                            sm={3}
                            className={this.props.classes.guarantyBoxOptional}
                            style={{ borderRight: 0 }}
                        >
                            <div className={this.props.classes.guarantyBoxCheckbox}>
                                <InputCheckbox {...uiForm.fields.isITT} />
                            </div>
                            <div className={this.props.classes.guarantyBoxInput}>
                                <Grid container={true} spacing={8}>
                                    <Grid item={true} sm={6}>
                                        <InputSelect {...uiForm.fields.ITTFranchise} />
                                    </Grid>
                                    <Grid item={true} sm={6}>
                                        <InputSelect {...uiForm.fields.ITTGuarantee} />
                                    </Grid>
                                </Grid>
                            </div>
                            <div className={this.props.classes.guarantyBoxResult}>
                                {product && product.ITTContribution ? (
                                    <Typography variant="body2" gutterBottom={true} align="center">
                                        {product.ITTContribution.toFixed(2)} €/an
                                    </Typography>
                                ) : null}
                            </div>
                        </Grid>

                        {/* IPT */}

                        <Grid
                            item={true}
                            sm={3}
                            className={this.props.classes.guarantyBoxOptional}
                            style={{ borderRight: 0 }}
                        >
                            <div className={this.props.classes.guarantyBoxCheckbox}>
                                <InputCheckbox {...uiForm.fields.isIPT} />
                                <div style={{ fontSize: "0.6rem", position: "absolute" }}>
                                    La garantie ITT est couplée à la garantie IPT
                                </div>
                            </div>
                            <div className={this.props.classes.guarantyBoxInput}>
                                <InputText {...uiForm.fields.IPTGuarantee} />
                            </div>
                            <div className={this.props.classes.guarantyBoxResult}>
                                {product && product.IPTContribution ? (
                                    <Typography variant="body2" gutterBottom={true} align="center">
                                        {product.IPTContribution.toFixed(2)} €/an
                                    </Typography>
                                ) : null}
                            </div>
                        </Grid>

                        {/* Total */}

                        <div className={this.props.classes.guarantyBoxTotal}>
                            <Typography variant="body1" align="right" style={{ color: "white" }}>
                                Cotisation annuelle totale TTC :
                                {product && product.contribution ? (
                                    <b>&nbsp; {product.contribution.toFixed(2)} €</b>
                                ) : null}
                            </Typography>
                        </div>
                    </Grid>
                </div>

                {/* Cotisations */}

                {product && product.contribution ? (
                    <Grid item={true} sm={12}>
                        <div className={this.props.classes.box}>
                            <Typography
                                variant={this.props.mode === "calculator" ? "headline" : "title"}
                                gutterBottom={true}
                                className={this.props.classes.formTitle}
                            >
                                {this.props.mode === "calculator" ? "Cotisations" : "6/ Cotisations"}
                            </Typography>
                            {this.props.mode !== "calculator" && (
                                <div className={this.props.classes.cotisationsBox}>
                                    <div>
                                        <Typography variant="body1" className={this.props.classes.cotisationsBoxTitle}>
                                            <b>Rappel : Déclaration d’état de santé</b>
                                            <br />
                                        </Typography>
                                        {/* <InputRadio {...uiForm.fields.healthState} /> */}
                                    </div>
                                    <Typography variant="body1" className={this.props.classes.cotisationsBoxItem}>
                                        L’assuré doit faire parvenir sa Déclaration d’Etat de Santé (et son
                                        questionnaire médical si nécessaire) au médecin conseil dans l’enveloppe
                                        confidentielle prévue à cet effet.
                                    </Typography>
                                    <Typography variant="body1" className={this.props.classes.cotisationsBoxItem}>
                                        À défaut, sa demande d’adhésion sera refusée. Votre profil médical sera étudié
                                        par le médecin conseil d’AG2R LA MONDIALE. Celui-ci statuera sur :
                                    </Typography>
                                    <Typography variant="body1" className={this.props.classes.cotisationsBoxSubItem}>
                                        • L’acceptation de votre adhésion sans surprime.
                                    </Typography>
                                    <Typography variant="body1" className={this.props.classes.cotisationsBoxSubItem}>
                                        • L’application d’une surprime. Dans ce cas, vous recevrez alors une nouvelle
                                        proposition d’adhésion à retourner signée dans l’enveloppe jointe.
                                    </Typography>
                                    <Typography variant="body1" className={this.props.classes.cotisationsBoxSubItem}>
                                        • Le refus. La présente demande d’adhésion sera alors considérée comme caduque.
                                        Vous recevrez alors un courrier d’information.
                                    </Typography>
                                </div>
                            )}
                            <div className={this.props.classes.cotisationsBoxResume}>
                                <Typography variant="display3" className={this.props.classes.cotisationsBoxResumeItem}>
                                    <b>RÉSUMÉ</b>
                                </Typography>
                                <Typography variant="display3" className={this.props.classes.cotisationsBoxResumeItem}>
                                    Votre cotisation périodique sera de{" "}
                                    <Contribution
                                        value={this.props.productHelper.getPeriodicContribution()}
                                        interval={product.samplingInterval}
                                    />{" "}
                                    (assistance incluse) et sera due par prélèvement bancaire.
                                </Typography>
                            </div>
                        </div>
                    </Grid>
                ) : null}

                {/* Bénéficiaires */}

                {this.props.mode !== "calculator" && product && product.contribution ? (
                    <Grid item={true} sm={12}>
                        <div className={this.props.classes.box}>
                            <Typography variant="title" gutterBottom={true} className={this.props.classes.formTitle}>
                                7/ Bénéficiaires
                            </Typography>
                            <Grid container={true} spacing={16}>
                                <Grid item={true} sm={12}>
                                    <div>
                                        <InputRadio {...uiForm.fields.beneficiaryClause} />
                                    </div>
                                </Grid>
                                {product && product.beneficiaryClause === "beneficiary" ? (
                                    <Grid item={true} sm={12}>
                                        <QuotationFormBeneficiaries
                                            classes={this.props.classes}
                                            beneficiaries={product.beneficiaries}
                                            onChange={this.onChangeBeneficiaries.bind(this)}
                                        />
                                    </Grid>
                                ) : null}
                            </Grid>
                        </div>
                    </Grid>
                ) : null}
            </>
        );
    }

    private updateState(props: QuotationProductGPIProps) {
        const product = this.updateProduct(props);

        this.formBuilder = new UiFormBuilder<Products>();

        this.formBuilder

            // Mode de prélèvement

            .addField("samplingInterval", {
                props: {
                    items: productGPISamplingIntervalsItems,
                },
            })
            .addField("samplingDate", {
                props: {
                    label: "Le prélèvement sera effectué le :",
                    items: productGPISamplingDatesItems,
                },
            })

            // IAD

            .addField("isIAD", {
                props: {
                    label: "Garantie Décès-Invalidité Absolue et Définitive (IAD) y compris la Garantie Assistance",
                    required: true,
                    readonly: true,
                },
            })
            .addField("IADGuarantee", {
                props: {
                    label: "Niveau des garanties",
                    items: this.props.productHelper.IADGuaranteeItems(),
                    required: true,
                },
            })
            .addField("IADContribution", {
                props: {
                    label: "Montant",
                    disabled: true,
                    description: "/ans",
                },
            })

            // Accident

            .addField("isAccident", {
                props: {
                    disabled: !product.IADGuarantee || product.IADGuarantee < 15000,
                    label: "Garantie Doublement en cas d'accident",
                },
            })
            .addField("accidentGuarantee", {
                props: {
                    label: "Montant des garanties",
                    disabled: true,
                    description: "€",
                },
            })
            .addField("accidentContribution", {
                props: {
                    label: "Montant",
                    disabled: true,
                    description: "/ans",
                },
            })

            // ITT

            .addField("isITT", {
                props: {
                    disabled:
                        !product.IADGuarantee ||
                        product.IADGuarantee < 15000 ||
                        this.props.productHelper.getAgeAtEffectiveDate() >= 62,
                    label: "Garantie Incapacité Temporaire Totale de travail (ITT)",
                },
            })
            .addField("ITTFranchise", {
                props: {
                    label: "Franchise",
                    disabled: !product.isITT,
                    items: this.props.productHelper.ITTFranchiseItems(),
                },
            })
            .addField("ITTGuarantee", {
                props: {
                    label: "Garanties",
                    disabled: !product.isITT,
                    items: this.props.productHelper.ITTGuaranteeItems(),
                    description: product.ITTGuarantee ? "/jour" : "",
                },
            })
            .addField("ITTContribution", {
                props: {
                    label: "Montant",
                    disabled: true,
                    description: "/ans",
                },
            })

            // IPT

            .addField("isIPT", {
                props: {
                    readonly: true,
                    disabled: !product.ITTContribution,
                    label: "Garantie Invalidité Permanente Totale (IPT)",
                },
            })
            .addField("IPTGuarantee", {
                props: {
                    label: "Montant des garanties",
                    disabled: true,
                    description: "€/an",
                },
            })
            .addField("IPTContribution", {
                props: {
                    disabled: true,
                    label: "Montant",
                    description: "/ans",
                },
            })

            // Total

            .addField("healthState", {
                props: {
                    name: "healthState",
                    items: productGPIHealthStateItems,
                    required: true,
                },
            })

            .addField("healthOptin", {
                props: {
                    label: "Je consens à ce que mes données de santé soient utilisées dans le cadre de la gestion de mon contrat d'assurance.",
                },
            })

            .addField("signDeclaration", {
                props: {
                    name: "signDeclaration",
                    items: productGPISignDeclarationItems,
                    required: true,
                },
            })

            // Bénéficiaire

            .addField("beneficiaryClause", {
                props: {
                    name: "beneficiaryClause",
                    items: productSPRBeneficiaryClauseItems,
                    required: true,
                },
            });

        this.formBuilder.onChange(this.onChange.bind(this));

        this.setState({
            form: {
                fields: {},
                errors: [],
                values: product,
            },
        });
    }

    private updateProduct(props) {
        let product = props.quotation.product as ProductGPI;

        try {
            if (product.IADGuarantee) {
                product.IADContribution = this.props.productHelper.getIADContribution();
            }

            if (product.IADGuarantee && product.isAccident) {
                product.accidentGuarantee = product.IADGuarantee;
                product.accidentContribution = this.props.productHelper.getAccidentContribution();
            }

            if (!product.isAccident) {
                product.accidentGuarantee = null;
                product.accidentContribution = null;
            }

            if (product.isITT) {
                product.isIPT = true;
            }

            if (product.isITT && product.ITTFranchise && product.ITTGuarantee) {
                product.ITTContribution = this.props.productHelper.getITTContribution();
                product.IPTGuarantee = this.props.productHelper.getIPTGuarantee();
                product.IPTContribution = this.props.productHelper.getIPTContribution();
                product.isIPT = true;
            }

            if (!product.isITT) {
                product.isIPT = false;
                product.ITTFranchise = null;
                product.ITTGuarantee = null;
                product.ITTContribution = null;
                product.IPTContribution = null;
                product.IPTGuarantee = null;
            }

            if (product.IADContribution) {
                product.contribution = this.props.productHelper.getContribution();
            }

            this.props.hideError();
        } catch (e) {
            window.console.log(e);
            this.props.showError(e.message);
        }

        return product;
    }
}

export default QuotationFormProductGPI;



// WEBPACK FOOTER //
// ./src/components/QuotationForm/QuotationFormProductGPI.tsx